import React, { useState, useEffect } from 'react';
import { getErrorMessage } from '@utils/commons';
import { useErrors } from '@contexts/ErrorsContext';
import { DateObjectType } from '@typings/common';

interface UpdatedDateProps {
  planUpdatedDate: DateObjectType;
  setPlanUpdatedDate: React.Dispatch<React.SetStateAction<DateObjectType>>;
}

const PlanUpdatedDate: React.FC<UpdatedDateProps> = ({
  planUpdatedDate,
  setPlanUpdatedDate,
}) => {
  const [planUpdatedDateErrMessage, setPlanUpdatedDateErrMessage] = useState<
    string | undefined
  >('');
  const { errors } = useErrors();

  useEffect(() => {
    setPlanUpdatedDateErrMessage(getErrorMessage(errors, 'planUpdatedDate'));
  }, [errors]);

  const handleDateChange = (field: 'day' | 'month' | 'year', value: string) => {
    setPlanUpdatedDate(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div
      className={`govuk-form-group mt-10 govuk-bp:mt-14 ${planUpdatedDateErrMessage ? 'govuk-form-group--error' : ''}`}
    >
      <fieldset
        className="govuk-fieldset"
        role="group"
        aria-describedby="plan-updated-date-hint"
      >
        <legend className="govuk-fieldset__legend govuk-fieldset__legend--l">
          <h1 className="govuk-fieldset__heading">
            The date this plan was last updated
          </h1>
        </legend>
        <div id="plan-updated-date-hint" className="govuk-hint">
          For example, 31 3 2026
        </div>
        {planUpdatedDateErrMessage && (
          <p id="full-name-input-error" className="govuk-error-message">
            <span className="govuk-visually-hidden">Error:</span>
            {planUpdatedDateErrMessage}
          </p>
        )}
        <div className="govuk-date-input__item">
          <div className="govuk-form-group">
            <label
              className="govuk-label govuk-date-input__label"
              htmlFor="planUpdatedDate"
            >
              Day
            </label>

            <input
              className={`govuk-input govuk-date-input__input govuk-input--width-2 ${planUpdatedDateErrMessage ? 'govuk-input--error' : ''}`}
              id="planUpdatedDate"
              name="planUpdatedDate"
              type="text"
              inputMode="numeric"
              maxLength={2}
              placeholder="DD"
              value={planUpdatedDate.day}
              onChange={e => {
                const { value } = e.target;
                if (/^[0-9]*$/.test(value) && value.length <= 2) {
                  handleDateChange('day', value);
                }
              }}
            />
          </div>
        </div>
        <div className="govuk-date-input__item">
          <div className="govuk-form-group">
            <label
              className="govuk-label govuk-date-input__label"
              htmlFor="plan-updated-date-month"
            >
              Month
            </label>
            <input
              className={`govuk-input govuk-date-input__input govuk-input--width-2 ${planUpdatedDateErrMessage ? 'govuk-input--error' : ''}`}
              id="plan-updated-date-month"
              name="plan-updated-date-month"
              type="text"
              inputMode="numeric"
              placeholder="MM"
              maxLength={2}
              value={planUpdatedDate.month}
              onChange={e => {
                const { value } = e.target;
                if (/^[0-9]*$/.test(value) && value.length <= 2) {
                  handleDateChange('month', value);
                }
              }}
            />
          </div>
        </div>
        <div className="govuk-date-input__item">
          <div className="govuk-form-group">
            <label
              className="govuk-label govuk-date-input__label"
              htmlFor="plan-updated-date-year"
            >
              Year
            </label>
            <input
              className={`govuk-input govuk-date-input__input govuk-input--width-4 ${planUpdatedDateErrMessage ? 'govuk-input--error' : ''}`}
              id="plan-updated-date-year"
              name="plan-updated-date-year"
              type="text"
              inputMode="numeric"
              placeholder="YYYY"
              maxLength={4}
              value={planUpdatedDate.year}
              onChange={e => {
                const { value } = e.target;
                if (/^[0-9]*$/.test(value) && value.length <= 4) {
                  handleDateChange('year', value);
                }
              }}
            />
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default PlanUpdatedDate;
